import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Receipt, Sparkles, ArrowUpRight, FileSignature, CheckCircle2, Ban, Archive } from "lucide-react";
import { get, post, toastError } from "@/lib/api";
import { nok, fmtDate, fmtDateTime } from "@/lib/format";
import { label } from "@/lib/labels";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useAssistantOptional } from "@/context/AssistantContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BackLink, PageHeader, StatusSteps, Section, KpiTile, KpiGrid } from "@/components/common/Page";
import { LoadingState, ErrorState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DataTable } from "@/components/common/DataTable";
import { ActivityFeed } from "@/components/common/ActivityFeed";
import { Money, Row, DueCell, Provenance } from "@/components/finance/FinanceBits";
import { CONTRACT_STEPS, compactKr } from "@/components/finance/money";

const stepOf = (s) => (s === "utlopt" || s === "oppsagt" ? "avsluttet" : s);

export default function ContractDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const assistant = useAssistantOptional();
  const q = useQuery({ queryKey: ["finance", "contract", id], queryFn: () => get(`/finance/contracts/${id}`) });
  const c = q.data;
  const [genOpen, setGenOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [note, setNote] = useState("");

  const gen = useMutation({
    mutationFn: () => post(`/finance/contracts/${id}/generate-invoice`, {}),
    onSuccess: (inv) => {
      toast.success(`Fakturautkast ${inv.invoice_number} opprettet`, { description: `${nok(inv.total)} inkl. mva · ${fmtDate(inv.period_start, "d. MMM")} – ${fmtDate(inv.period_end, "d. MMM")}` });
      qc.invalidateQueries({ queryKey: ["finance"] });
      setGenOpen(false);
      navigate(`/okonomi/fakturaer/${inv.id}`);
    },
    onError: (e) => toastError(e, "Kunne ikke generere faktura"),
  });
  const st = useMutation({
    mutationFn: ({ status, note: n }) => post(`/finance/contracts/${id}/status`, { status, note: n || null }),
    onSuccess: (_, v) => {
      toast.success(`Kontrakten er ${label(v.status).toLowerCase()}`);
      qc.invalidateQueries({ queryKey: ["finance"] });
      setStatusTarget(null);
      setNote("");
    },
    onError: (e) => toastError(e, "Kunne ikke endre status"),
  });

  if (q.isLoading) return <LoadingState className="py-32" />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;

  const canWrite = can("finance:write");
  const nextStatus = { utkast: ["signert"], signert: ["aktiv", "oppsagt"], aktiv: ["oppsagt", "avsluttet"], utlopt: ["avsluttet"] }[c.status] || [];
  const STATUS_ICON = { signert: FileSignature, aktiv: CheckCircle2, oppsagt: Ban, avsluttet: Archive };
  const STATUS_VERB = { signert: "Marker signert", aktiv: "Aktiver", oppsagt: "Si opp", avsluttet: "Avslutt" };
  const invCols = [
    { key: "invoice_number", header: "Faktura", render: (r) => <span className="font-medium tabular">{r.invoice_number}</span> },
    { key: "period", header: "Periode", render: (r) => (r.period_start ? <span className="whitespace-nowrap tabular">{fmtDate(r.period_start, "d. MMM yy")} – {fmtDate(r.period_end, "d. MMM yy")}</span> : <span className="text-muted-foreground">{r.title?.split(" – ").pop()}</span>) },
    { key: "due_date", header: "Forfall", render: (r) => <DueCell date={r.due_date} days={r.days_overdue} /> },
    { key: "total", header: "Beløp", align: "right", render: (r) => <Money v={r.kind === "kreditnota" ? -r.total : r.total} /> },
    { key: "outstanding", header: "Utestående", align: "right", render: (r) => <Money v={r.outstanding} muted={!r.outstanding} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} entity="invoice" /> },
  ];

  return (
    <div data-testid="contract-detail-page">
      <BackLink to="/okonomi/kontrakter" testId="contract-back-link">Kontrakter</BackLink>
      <PageHeader
        className="mt-2"
        eyebrow={`${label(c.type)} · ${c.customer_name}${c.project ? ` · ${c.project.project_number}` : ""}`}
        title={<span className="inline-flex items-baseline gap-3"><span className="tabular">{c.contract_number}</span><StatusBadge status={c.status} entity="contract" variant="pill" /></span>}
        subtitle={c.title}
        actions={
          <>
            {assistant && <Button variant="ghost" onClick={() => assistant.ask(`Oppsummer kontrakt ${c.contract_number} – verdi, oppsigelsesfrist, opsjoner, fakturert/utestående og neste fakturaperiode`, { contract_id: c.id })} data-testid="contract-ask-ai-button"><Sparkles /> Spør AI</Button>}
            {canWrite && nextStatus.map((s) => { const I = STATUS_ICON[s]; return <Button key={s} variant={s === "aktiv" || s === "signert" ? "default" : "outline"} onClick={() => setStatusTarget(s)} data-testid={`contract-status-${s}-button`}><I /> {STATUS_VERB[s]}</Button>; })}
            {canWrite && ["aktiv", "signert"].includes(c.status) && c.monthly_value > 0 && (
              <Button onClick={() => setGenOpen(true)} disabled={!c.next_period} title={!c.next_period ? "Alle perioder til og med neste måned er fakturert" : undefined} data-testid="contract-generate-invoice-button"><Receipt /> Generer faktura</Button>
            )}
          </>
        }
        testId="contract-header"
      />

      <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-8">
        <div className="min-w-0 lg:col-span-8">
          <StatusSteps steps={CONTRACT_STEPS} current={stepOf(c.status)} labelFn={label} className="max-w-md" testId="contract-steps" />
          <KpiGrid className="mt-8" testId="contract-kpis">
            <KpiTile label="Per måned" value={compactKr(c.monthly_value)} hint={`${c.lines?.filter((l) => l.recurring).length || 0} løpende linjer`} testId="contract-kpi-monthly" />
            <KpiTile label="Totalverdi" value={compactKr(c.total_value)} hint={`${c.months} mnd · ${compactKr(c.one_off_value)} engang`} testId="contract-kpi-total" />
            <KpiTile label="Fakturert eks. mva" value={compactKr(c.invoiced_total)} hint={`${c.invoices?.length || 0} fakturaer`} testId="contract-kpi-invoiced" />
            <KpiTile label="Utestående" value={compactKr(c.outstanding)} tone={c.invoices?.some((i) => i.status === "forfalt") ? "danger" : "default"} hint={c.invoices?.some((i) => i.status === "forfalt") ? "inneholder forfalt" : "inkl. mva"} testId="contract-kpi-outstanding" />
          </KpiGrid>

          {c.next_period && ["aktiv", "signert"].includes(c.status) && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-primary/[0.06] px-4 py-3 text-[13px]" data-testid="contract-next-period">
              <div>
                <span className="font-medium">Neste fakturaperiode:</span> {fmtDate(c.next_period.period_start, "d. MMMM")} – {fmtDate(c.next_period.period_end, "d. MMMM yyyy")}
                <span className="text-muted-foreground"> · forskuddsfakturering, {c.payment_terms_days} dagers frist</span>
              </div>
              {canWrite && <Button size="sm" onClick={() => setGenOpen(true)} data-testid="contract-next-period-generate"><Receipt /> Lag utkast</Button>}
            </div>
          )}

          <Section title="Kontraktslinjer" className="mt-8" testId="contract-lines" description="Løpende linjer faktureres hver periode · engangslinjer ved oppstart/avslutning">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11.5px] text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Beskrivelse</th>
                  <th className="px-3 py-2 text-right font-medium">Antall</th>
                  <th className="px-3 py-2 text-right font-medium">Pris</th>
                  <th className="px-3 py-2 font-medium">Frekvens</th>
                  <th className="px-4 py-2 text-right font-medium">Beløp</th>
                </tr>
              </thead>
              <tbody>
                {(c.lines || []).map((l) => (
                  <tr key={l.id} className="border-t border-border/60" data-testid="contract-line">
                    <td className="px-4 py-2"><div>{l.description}</div><div className="text-[11.5px] text-muted-foreground">{label(l.kind)}{l.module_type_code ? ` · ${l.module_type_code}` : ""}</div></td>
                    <td className="px-3 py-2 text-right tabular">{l.quantity} {l.unit}</td>
                    <td className="px-3 py-2 text-right tabular">{nok(l.unit_price)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.recurring ? label(c.billing_cadence) : "Engang"}</td>
                    <td className="px-4 py-2 text-right font-medium tabular">{nok(l.quantity * l.unit_price)}{l.recurring ? <span className="text-muted-foreground">/mnd</span> : ""}</td>
                  </tr>
                ))}
                {!c.lines?.length && <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">Rammeavtale uten faste linjer – vilkår under.</td></tr>}
              </tbody>
            </table>
          </Section>

          <div className="mt-8">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-[13.5px] font-semibold tracking-tight">Fakturaer på kontrakten</h2>
              <Link to={`/okonomi/fakturaer?contract_id=${c.id}`} className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground" data-testid="contract-invoices-link">Alle <ArrowUpRight className="h-3.5 w-3.5" /></Link>
            </div>
            <DataTable columns={invCols} rows={(c.invoices || []).slice(0, 12)} onRowClick={(r) => navigate(`/okonomi/fakturaer/${r.id}`)} rowTestId={(r) => `contract-invoice-${r.invoice_number}`} empty="Ingen fakturaer enda" testId="contract-invoices-table" />
          </div>

          <div className="mt-8"><ActivityFeed entityType="contract" entityId={c.id} allowAdd={canWrite} /></div>
        </div>

        <aside className="min-w-0 space-y-8 lg:col-span-4 lg:border-l lg:border-border/70 lg:pl-8" data-testid="contract-sidebar">
          <div>
            <h2 className="text-[13.5px] font-semibold tracking-tight">Vilkår</h2>
            <div className="mt-2 divide-y divide-border/60">
              <Row label="Periode" mono>{fmtDate(c.start_date)} – {c.end_date ? fmtDate(c.end_date) : "løpende"}</Row>
              {c.days_to_end != null && c.status === "aktiv" && <Row label="Gjenstår"><span className={cn(c.days_to_end <= 60 && "font-medium text-amber-700 dark:text-amber-300")}>{c.days_to_end} dager</span></Row>}
              {c.min_period_months && <Row label="Minste leietid">{c.min_period_months} mnd</Row>}
              <Row label="Oppsigelsestid">{c.notice_period_days} dager{c.notice_deadline ? ` · frist ${fmtDate(c.notice_deadline)}` : ""}</Row>
              <Row label="Fakturering">{label(c.billing_cadence)}{c.billing_in_advance ? " · forskudd" : ""} · {c.payment_terms_days} d</Row>
              <Row label="Indeksregulering">{label(c.indexation_type)}{c.indexation_next_date ? ` · ${fmtDate(c.indexation_next_date)}` : ""}</Row>
              <Row label="Garanti">{label(c.guarantee_type)}{c.deposit_amount ? ` · ${nok(c.deposit_amount)}` : ""}</Row>
              {c.po_number && <Row label="PO" mono>{c.po_number}</Row>}
              {c.customer_reference && <Row label="Deres ref">{c.customer_reference}</Row>}
            </div>
          </div>
          <div>
            <h2 className="text-[13.5px] font-semibold tracking-tight">Parter</h2>
            <div className="mt-2 divide-y divide-border/60">
              <Row label="Kunde"><Link to={`/kunder/${c.customer_id}`} className="inline-flex items-center gap-1 hover:underline" data-testid="contract-customer-link">{c.customer?.name} <ArrowUpRight className="h-3 w-3 text-muted-foreground" /></Link></Row>
              {c.project && <Row label="Prosjekt"><Link to={`/prosjekter/${c.project.id}`} className="inline-flex items-center gap-1 hover:underline" data-testid="contract-project-link">{c.project.name} <ArrowUpRight className="h-3 w-3 text-muted-foreground" /></Link></Row>}
              {c.project && <Row label="Økonomi"><Link to={`/okonomi/prosjekter/${c.project.id}`} className="inline-flex items-center gap-1 hover:underline" data-testid="contract-economics-link">Prosjektøkonomi <ArrowUpRight className="h-3 w-3 text-muted-foreground" /></Link></Row>}
              {c.quote && <Row label="Tilbud"><Link to={`/tilbud/${c.quote.id}`} className="hover:underline tabular">{c.quote.quote_number}</Link></Row>}
              <Row label="Signert av kunde">{c.signed_by_customer}</Row>
              <Row label="Signert av oss">{c.signed_by_us}</Row>
              <Row label="Signert" mono>{c.signed_at ? fmtDate(c.signed_at) : null}</Row>
            </div>
          </div>
          {c.options?.length > 0 && (
            <div>
              <h2 className="text-[13.5px] font-semibold tracking-tight">Opsjoner</h2>
              <ul className="mt-2 space-y-2 text-[13px]">
                {c.options.map((o, i) => <li key={i} className="flex items-start gap-2"><span className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", o.exercised ? "bg-emerald-500" : "bg-foreground/30")} aria-hidden /><span>{o.description}{o.exercised ? " · utløst" : ""}</span></li>)}
              </ul>
            </div>
          )}
          {(c.inclusions || c.exclusions) && (
            <div className="space-y-3 text-[13px]">
              {c.inclusions && <div><div className="text-[12px] font-medium text-muted-foreground">Inkludert</div><p className="mt-0.5 leading-5">{c.inclusions}</p></div>}
              {c.exclusions && <div><div className="text-[12px] font-medium text-muted-foreground">Ikke inkludert</div><p className="mt-0.5 leading-5">{c.exclusions}</p></div>}
            </div>
          )}
          {c.status_history?.length > 0 && (
            <div>
              <h2 className="text-[13.5px] font-semibold tracking-tight">Historikk</h2>
              <ol className="mt-2 space-y-1.5 text-[12.5px]">
                {[...c.status_history].reverse().map((h, i) => <li key={i} className="flex justify-between gap-3"><span>{label(h.status)}{h.note ? ` · ${h.note}` : ""}</span><span className="shrink-0 text-muted-foreground tabular">{h.by ? `${h.by} · ` : ""}{fmtDateTime(h.at)}</span></li>)}
              </ol>
            </div>
          )}
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground"><Provenance kind="prognose" /> Måneds- og totalverdi er kontraktsfestet prognose</div>
        </aside>
      </div>

      <AlertDialog open={genOpen} onOpenChange={setGenOpen}>
        <AlertDialogContent data-testid="contract-generate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Generer fakturautkast</AlertDialogTitle>
            <AlertDialogDescription>
              Periode {c.next_period ? `${fmtDate(c.next_period.period_start, "d. MMMM")} – ${fmtDate(c.next_period.period_end, "d. MMMM yyyy")}` : "–"} · løpende linjer ({compactKr(c.monthly_value)}/mnd eks. mva, pro rata ved delperiode). Utkastet kan gjennomgås før sending.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="contract-generate-confirm">Lag utkast</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!statusTarget} onOpenChange={(v) => !v && setStatusTarget(null)}>
        <AlertDialogContent data-testid="contract-status-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{STATUS_VERB[statusTarget] || "Endre status"}?</AlertDialogTitle>
            <AlertDialogDescription>{c.contract_number} går fra «{label(c.status)}» til «{label(statusTarget)}». Endringen logges.</AlertDialogDescription>
          </AlertDialogHeader>
          {(statusTarget === "oppsagt" || statusTarget === "avsluttet") && <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Merknad (valgfritt)" data-testid="contract-status-note" />}
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => st.mutate({ status: statusTarget, note })} disabled={st.isPending} data-testid="contract-status-confirm">Bekreft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
